import React, { useState } from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    Typography,
    Box,
    Alert,
    List,
    ListItem,
    ListItemText,
    CircularProgress
} from '@mui/material';

/**
 * Component for confirmation dialog when deleting a data source
 */
const DeleteConfirmationDialog = ({
    open,
    onClose,
    onConfirm,
    sourceToDelete
}) => {
    const isFileConnection = sourceToDelete?.type === 'Excel';
    // El borrado puede tardar varios segundos (modelo semántico, tablas de
    // Glue y objetos de S3): mientras dura, el diálogo muestra el avance y
    // no se puede cerrar ni volver a confirmar.
    const [isDeleting, setIsDeleting] = useState(false);

    const handleConfirm = async () => {
        setIsDeleting(true);
        try {
            await onConfirm();
        } finally {
            setIsDeleting(false);
        }
    };

    const handleClose = () => {
        if (!isDeleting) onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            disableEscapeKeyDown={isDeleting}
            PaperProps={{
                sx: {
                    bgcolor: 'var(--wb-bg-panel)',
                    color: 'var(--wb-text-strong)',
                }
            }}
        >
            <DialogTitle sx={{ borderBottom: '1px solid var(--wb-overlay-10)' }}>
                Confirmar eliminación
            </DialogTitle>
            <DialogContent sx={{ mt: 2 }}>
                <Typography variant="body1">
                    ¿Estás seguro de que deseas eliminar la fuente de datos
                    <Box component="span" sx={{ fontWeight: 'bold', mx: 0.5 }}>
                        {sourceToDelete?.name}
                    </Box>?
                </Typography>

                {isFileConnection && (
                    <Alert
                        severity="warning"
                        sx={{
                            mt: 2,
                            bgcolor: 'var(--wb-x-bg-ffc1071a, rgba(255, 193, 7, 0.1))',
                            color: 'var(--wb-warning-strong)',
                            '& .MuiAlert-icon': { color: 'var(--wb-warning-strong)' }
                        }}
                    >
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            Esto también eliminará permanentemente:
                        </Typography>
                        <List dense sx={{ py: 0, listStyleType: 'disc', pl: 2 }}>
                            <ListItem sx={{ display: 'list-item', p: 0 }}>
                                <ListItemText primary="El archivo cargado y la tabla generada en Athena/Glue" />
                            </ListItem>
                            <ListItem sx={{ display: 'list-item', p: 0 }}>
                                <ListItemText primary="La entidad semántica (semantic model) asociada" />
                            </ListItem>
                        </List>
                    </Alert>
                )}

                {isDeleting ? (
                    <Box
                        role="status"
                        aria-live="polite"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            mt: 2,
                            p: 1.5,
                            borderRadius: 1,
                            bgcolor: 'var(--wb-x-bg-0095ff14, rgba(0, 149, 255, 0.08))',
                            border: '1px solid var(--wb-x-border-0095ff4c, rgba(0, 149, 255, 0.3))'
                        }}
                    >
                        <CircularProgress size={20} sx={{ color: 'var(--wb-primary)', flexShrink: 0 }} />
                        <Typography variant="body2" sx={{ color: 'var(--wb-x-text-ffffffd9, rgba(255, 255, 255, 0.85))' }}>
                            {isFileConnection
                                ? 'Eliminando la conexión y sus recursos (archivo, tablas y modelo semántico). Esto puede tardar unos segundos…'
                                : 'Eliminando la conexión…'}
                        </Typography>
                    </Box>
                ) : (
                    <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'var(--wb-text-dim)' }}>
                        Esta acción no se puede deshacer.
                    </Typography>
                )}
            </DialogContent>
            <DialogActions sx={{ p: 2, borderTop: '1px solid var(--wb-overlay-10)' }}>
                <Button
                    onClick={handleClose}
                    disabled={isDeleting}
                    sx={{ color: 'var(--wb-text-soft)' }}
                >
                    Cancelar
                </Button>
                <Button
                    onClick={handleConfirm}
                    disabled={isDeleting}
                    variant="contained"
                    startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : null}
                    sx={{
                        bgcolor: 'var(--wb-danger-strong)',
                        '&:hover': { bgcolor: 'var(--wb-x-bg-d32f2f, #d32f2f)' },
                        '&.Mui-disabled': { bgcolor: 'var(--wb-x-bg-f4433680, rgba(244, 67, 54, 0.5))', color: 'var(--wb-x-text-ffffffcc, rgba(255, 255, 255, 0.8))' }
                    }}
                >
                    {isDeleting ? 'Eliminando…' : 'Eliminar'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DeleteConfirmationDialog;
